export const REPORT_PRIORITY_POLICIES = Object.freeze({
  SHIFT: "shift",
  SWAP: "swap",
  REJECT: "reject",
});

export const DEFAULT_REPORT_PRIORITY_POLICY = REPORT_PRIORITY_POLICIES.SHIFT;

export const normalizeReportPriorityPolicy = (value) => {
  const policy = typeof value === "string" ? value : value?.conflictPolicy;
  return Object.values(REPORT_PRIORITY_POLICIES).includes(policy)
    ? policy
    : DEFAULT_REPORT_PRIORITY_POLICY;
};

const normalizePriority = (value, fallback) => {
  const priority = Number(value);
  return Number.isInteger(priority) && priority > 0 ? priority : fallback;
};

export const findReportPriorityConflict = (datastreams, datastream) => {
  const priority = normalizePriority(datastream?.reportPriority, null);
  if (!priority) return null;
  return (Array.isArray(datastreams) ? datastreams : []).find(
    (candidate) =>
      String(candidate?.id) !== String(datastream?.id) &&
      normalizePriority(candidate?.reportPriority, null) === priority
  ) || null;
};

export const applyReportPriorityChange = (
  datastreams,
  changedDatastream,
  policy = DEFAULT_REPORT_PRIORITY_POLICY
) => {
  const normalizedPolicy = normalizeReportPriorityPolicy(policy);
  const existing = (Array.isArray(datastreams) ? datastreams : []).filter(Boolean);
  const sourceIndex = existing.findIndex(
    (datastream) => String(datastream?.id) === String(changedDatastream?.id)
  );
  const isNew = sourceIndex < 0;
  const base = existing.map((datastream, index) => ({
    ...datastream,
    reportPriority: normalizePriority(datastream?.reportPriority, index + 1),
  }));
  const sourcePriority = isNew ? base.length + 1 : base[sourceIndex].reportPriority;
  const targetPriority = Math.max(
    1,
    normalizePriority(changedDatastream?.reportPriority, sourcePriority)
  );
  const nextDatastream = { ...changedDatastream, reportPriority: targetPriority };
  const conflictIndex = base.findIndex(
    (datastream, index) => index !== sourceIndex && datastream.reportPriority === targetPriority
  );

  if (conflictIndex >= 0 && normalizedPolicy === REPORT_PRIORITY_POLICIES.REJECT) {
    return base;
  }

  if (normalizedPolicy === REPORT_PRIORITY_POLICIES.SWAP) {
    const next = isNew ? [...base, nextDatastream] : base.map((datastream, index) =>
      index === sourceIndex ? nextDatastream : datastream
    );
    if (conflictIndex >= 0) {
      next[conflictIndex] = { ...next[conflictIndex], reportPriority: sourcePriority };
    }
    return next;
  }

  const next = isNew
    ? [...base, nextDatastream]
    : base.map((datastream, index) => index === sourceIndex ? nextDatastream : datastream);

  if (conflictIndex < 0) return next;

  const priorityToIndex = new Map();
  base.forEach((datastream, index) => {
    if (index !== sourceIndex) priorityToIndex.set(datastream.reportPriority, index);
  });

  let displacedIndex = conflictIndex;
  let nextPriority = targetPriority + 1;
  while (displacedIndex !== undefined) {
    const nextDisplacedIndex = priorityToIndex.get(nextPriority);
    next[displacedIndex] = { ...next[displacedIndex], reportPriority: nextPriority };
    priorityToIndex.set(nextPriority, displacedIndex);
    displacedIndex = nextDisplacedIndex;
    nextPriority += 1;
  }

  return next;
};
